/* Brave Bottoms theme helpers: countdown, tabs, delivery estimate.
   Written as custom elements so they re-initialise automatically when the
   theme editor re-renders a section. No dependencies. */
(() => {
  const pad = (n) => String(n).padStart(2, '0');

  /* ---------- Countdown ---------- */
  class BBCountdown extends HTMLElement {
    connectedCallback() {
      this.end = Date.parse(this.dataset.end || '');
      this.parts = {
        d: this.querySelector('[data-u="d"]'),
        h: this.querySelector('[data-u="h"]'),
        m: this.querySelector('[data-u="m"]'),
        s: this.querySelector('[data-u="s"]'),
      };
      this.dayBox = this.querySelector('[data-box="d"]');
      if (Number.isNaN(this.end)) {
        this.finish();
        return;
      }
      this.tick();
      this.timer = window.setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    tick() {
      let t = Math.floor((this.end - Date.now()) / 1000);
      if (t <= 0) {
        this.finish();
        return;
      }
      const d = Math.floor(t / 86400);
      t %= 86400;
      const h = Math.floor(t / 3600);
      t %= 3600;
      const m = Math.floor(t / 60);
      const s = t % 60;
      if (this.dayBox) this.dayBox.hidden = d === 0;
      if (this.parts.d) this.parts.d.textContent = pad(d);
      if (this.parts.h) this.parts.h.textContent = pad(h);
      if (this.parts.m) this.parts.m.textContent = pad(m);
      if (this.parts.s) this.parts.s.textContent = pad(s);
      this.hidden = false;
    }

    finish() {
      window.clearInterval(this.timer);
      this.hidden = true;
      if (this.dataset.hideBar === 'true') {
        const bar = this.closest('[data-bb-bar]');
        if (bar) bar.hidden = true;
      }
    }
  }

  /* ---------- Tabs ---------- */
  class BBTabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.select(index));
        tab.addEventListener('keydown', (event) => {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
          event.preventDefault();
          const step = event.key === 'ArrowRight' ? 1 : -1;
          const next = (index + step + this.tabs.length) % this.tabs.length;
          this.select(next);
          this.tabs[next].focus();
        });
      });
    }

    select(index) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        const panel = this.querySelector(`#${tab.getAttribute('aria-controls')}`);
        if (panel) panel.hidden = !active;
      });
    }

    selectPanel(panel) {
      const index = this.tabs.findIndex((tab) => tab.getAttribute('aria-controls') === panel.id);
      if (index > -1) this.select(index);
    }
  }

  /* ---------- Delivery estimate (business days) ---------- */
  class BBEta extends HTMLElement {
    connectedCallback() {
      const min = parseInt(this.dataset.min || '7', 10);
      const max = parseInt(this.dataset.max || '12', 10);
      const addBusinessDays = (from, days) => {
        const date = new Date(from);
        let added = 0;
        while (added < days) {
          date.setDate(date.getDate() + 1);
          const day = date.getDay();
          if (day !== 0 && day !== 6) added += 1;
        }
        return date;
      };
      let format;
      try {
        format = new Intl.DateTimeFormat(document.documentElement.lang || 'en-US', { month: 'short', day: 'numeric' });
      } catch (error) {
        format = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
      }
      const now = new Date();
      const from = this.querySelector('[data-from]');
      const to = this.querySelector('[data-to]');
      if (from) from.textContent = format.format(addBusinessDays(now, Math.min(min, max)));
      if (to) to.textContent = format.format(addBusinessDays(now, Math.max(min, max)));
      this.hidden = false;
    }
  }

  if (!customElements.get('bb-countdown')) customElements.define('bb-countdown', BBCountdown);
  if (!customElements.get('bb-tabs')) customElements.define('bb-tabs', BBTabs);
  if (!customElements.get('bb-eta')) customElements.define('bb-eta', BBEta);

  /* Theme editor: open the tab or FAQ that the merchant selects */
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    const panel = target.closest('[role="tabpanel"]');
    const tabs = target.closest('bb-tabs');
    if (panel && tabs && typeof tabs.selectPanel === 'function') tabs.selectPanel(panel);
    const details = target.matches('details') ? target : target.querySelector('details');
    if (details) details.open = true;
  });

  /* ---------- Pack picker (Kaching Bundles) helpers ---------- */
  const packPicker = () => document.querySelector('.kaching-bundles');

  /* "Start potty training" style buttons point at #buy. Land on the pack
     picker itself, so shoppers on phones don't have to scroll past the gallery. */
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href="#buy"]') : null;
    if (!link) return;
    const picker = packPicker();
    if (!picker || !picker.getClientRects().length) return;
    event.preventDefault();
    const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 60;
    /* Wide screens scroll .page-wrapper instead of the window */
    const wrapper = document.querySelector('.page-wrapper');
    const inWrapper =
      wrapper && wrapper.scrollHeight > wrapper.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(wrapper).overflowY);
    const offset = inWrapper ? wrapper.scrollTop - wrapper.getBoundingClientRect().top : window.scrollY;
    const top = picker.getBoundingClientRect().top + offset - header - 16;
    (inWrapper ? wrapper : window).scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
  });

  /* Keep the sticky add-to-cart bar in step with the pack picked in Kaching,
     so it never shows "$30.00" while the 5 or 10 pair pack is selected. */
  const syncStickyBar = () => {
    const sticky = document.querySelector('sticky-add-to-cart');
    const picker = packPicker();
    const bar = picker && picker.querySelector('.kaching-bundles__bar--selected');
    if (!sticky || !bar) return;
    const subtitle = bar.querySelector('.kaching-bundles__bar-subtitle');
    const title = bar.querySelector('.kaching-bundles__bar-title');
    const price = bar.querySelector('.kaching-bundles__bar-price');
    const label = ((subtitle && subtitle.textContent) || (title && title.textContent) || '').trim();
    const variant = sticky.querySelector('.sticky-add-to-cart__variant');
    if (variant && label) {
      const text = label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
      if (variant.textContent.trim() !== text) variant.textContent = text;
    }
    const priceSlot =
      sticky.querySelector('.sticky-add-to-cart__price .price__regular .price') ||
      sticky.querySelector('.sticky-add-to-cart__price .price');
    if (priceSlot && price) {
      const amount = price.textContent.trim();
      if (amount && priceSlot.textContent.trim() !== amount) priceSlot.textContent = amount;
    }
  };

  let syncTimer = 0;
  const queueSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncStickyBar, 30);
  };

  const watchPackPicker = () => {
    const picker = packPicker();
    if (!picker || !picker.querySelector('.kaching-bundles__bar')) return false;
    const scope = picker.parentElement || picker;
    if (scope.dataset.bbWatched === 'true') return true;
    scope.dataset.bbWatched = 'true';
    new MutationObserver(queueSync).observe(scope, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    const sticky = document.querySelector('sticky-add-to-cart');
    if (sticky) new MutationObserver(queueSync).observe(sticky, { subtree: true, childList: true, characterData: true });
    syncStickyBar();
    return true;
  };

  if (document.querySelector('sticky-add-to-cart') || document.querySelector('a[href="#buy"]')) {
    let tries = 0;
    const poll = window.setInterval(() => {
      tries += 1;
      if (watchPackPicker() || tries > 60) window.clearInterval(poll);
    }, 500);
  }

  /* Open every product page on the 5 pair pack ("Buy 2, Get 3 Free") instead of
     Kaching's 1 pair default, unless the shopper has already picked a pack.
     Change PRESELECT_PAIRS to 10 to open on the 10 pair pack, or 0 to leave Kaching's default. */
  const PRESELECT_PAIRS = 5;
  let packPickedByShopper = false;
  document.addEventListener(
    'click',
    (event) => {
      if (event.isTrusted && event.target instanceof Element && event.target.closest('.kaching-bundles__bar')) {
        packPickedByShopper = true;
      }
    },
    true
  );
  const barPairs = (bar) => {
    const parts = bar.querySelectorAll('.kaching-bundles__bar-title, .kaching-bundles__bar-subtitle');
    const text = Array.from(parts, (el) => el.textContent).join(' ');
    const match = text.match(/(\d+)\s*pairs?\b/i);
    return match ? parseInt(match[1], 10) : 0;
  };
  const preselectPack = () => {
    const picker = packPicker();
    if (!picker) return false;
    const bars = Array.from(picker.querySelectorAll('.kaching-bundles__bar'));
    if (!bars.length) return false;
    if (PRESELECT_PAIRS <= 0 || packPickedByShopper || picker.dataset.bbPreselected === 'true') return true;
    picker.dataset.bbPreselected = 'true';
    const target = bars.find((bar) => barPairs(bar) === PRESELECT_PAIRS);
    if (target && !target.classList.contains('kaching-bundles__bar--selected')) {
      (target.querySelector('label') || target).click();
    }
    return true;
  };
  /* Multi-pair packs open with a different print on each pair (like UpAiry) instead of
     the same print on every row. Rows follow this order; edit it to change the mix.
     The mix is checked again shortly after it runs, because Kaching can put the print from a
     ?variant= link back into one row (that showed the same print twice). Once the shopper
     changes a print or size themselves, their picks are never overwritten. */
  const MIX_PRINTS = ['Watermelon', 'Rockets', 'Whales', 'Unicorns', 'Lions', 'Giraffes', 'Trucks', 'Butterflies', 'Bears', 'Flowers', 'Elephants', 'Moons'];
  const mixRuns = new Map();
  let printsTouched = false;
  ['pointerdown', 'keydown'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        if (event.isTrusted && event.target instanceof Element && event.target.closest('.kaching-bundles__bar-variants')) {
          printsTouched = true;
        }
      },
      true
    );
  });
  /* Kaching tells the theme about every print change, which would swap the main gallery photo
     to the last mixed print. Hold those messages back while the mix is applied. */
  let mixQuietUntil = 0;
  document.addEventListener(
    'shopify:product:select',
    (event) => {
      if (Date.now() < mixQuietUntil) event.stopImmediatePropagation();
    },
    true
  );
  const mixPrints = () => {
    const picker = packPicker();
    if (!picker) return;
    const bars = Array.from(picker.querySelectorAll('.kaching-bundles__bar'));
    const bar = bars.find((item) => item.classList.contains('kaching-bundles__bar--selected'));
    if (!bar) return;
    const index = bars.indexOf(bar);
    const selects = Array.from(bar.querySelectorAll('select[data-name="Print"]'));
    if (selects.length < 2) return;
    const runs = mixRuns.get(index) || 0;
    const allSame = new Set(selects.map((select) => select.value)).size === 1;
    /* After the shopper's own changes, only mix a pack that has never been mixed and still shows one print */
    if (printsTouched && !(allSame && runs === 0)) return;
    const desired = selects.map((select, i) => {
      const values = Array.from(select.options, (option) => option.value);
      const wanted = MIX_PRINTS.filter((name) => values.includes(name));
      return wanted.length ? wanted[i % wanted.length] : values[i % values.length];
    });
    if (selects.every((select, i) => !desired[i] || select.value === desired[i])) return;
    if (runs >= 4) return;
    mixRuns.set(index, runs + 1);
    mixQuietUntil = Date.now() + 1000;
    const urlBefore = window.location.href;
    window.setTimeout(() => {
      /* Kaching also writes the last mixed print into the address bar (?variant=). Put it back. */
      if (window.location.href !== urlBefore) window.history.replaceState(window.history.state, '', urlBefore);
    }, 1100);
    selects.forEach((select, i) => {
      const print = desired[i];
      if (print && select.value !== print) {
        select.value = print;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
    window.setTimeout(mixPrints, 700);
  };
  let mixTimer = 0;
  const watchPrintMix = () => {
    const picker = packPicker();
    if (!picker || picker.dataset.bbMixWatched === 'true') return;
    picker.dataset.bbMixWatched = 'true';
    new MutationObserver(() => {
      window.clearTimeout(mixTimer);
      mixTimer = window.setTimeout(mixPrints, 60);
    }).observe(picker, { subtree: true, attributes: true, attributeFilter: ['class'] });
    mixPrints();
  };

  if (/\/products\//.test(window.location.pathname)) {
    let presetTries = 0;
    const presetPoll = window.setInterval(() => {
      presetTries += 1;
      const ready = preselectPack();
      if (ready) {
        window.setTimeout(watchPrintMix, 150);
        window.clearInterval(presetPoll);
      } else if (presetTries > 150) {
        window.clearInterval(presetPoll);
      }
    }, 100);
  }
})();